import {
  NewTicketScreenshot,
  ScreenshotContentType,
  TICKET_SCREENSHOT_MAX_BYTES,
} from "features/support/tickets";

export interface PreparedScreenshot extends NewTicketScreenshot {
  /** A data: URL of the bytes being sent, so the thumbnail shows exactly that. */
  previewUrl: string;
}

const ACCEPTED_TYPES: ScreenshotContentType[] = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

export const SCREENSHOT_MAX_EDGE = 1920;

// Tried in order until one fits: quality first, since it costs less legibility
// than resolution does on a screenshot full of text.
const EDGE_STEPS = [SCREENSHOT_MAX_EDGE, 1600, 1280, 960, 640];
const QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4];

/** The size an image is drawn at so its longest edge is at most `maxEdge`. */
export const fitWithin = (
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } => {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/** `shot.png` -> `shot.jpg`; a name without an extension gains one. */
export const withJpegExtension = (filename: string): string => {
  const base = filename.replace(/\.[^./]*$/, "") || "screenshot";
  return `${base}.jpg`;
};

const isAcceptedType = (type: string): type is ScreenshotContentType =>
  (ACCEPTED_TYPES as string[]).includes(type);

const readAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the image."));
    reader.readAsDataURL(blob);
  });

const stripDataPrefix = (dataUrl: string) =>
  dataUrl.slice(dataUrl.indexOf(",") + 1);

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} is not an image this browser can read.`));
    };
    image.src = url;
  });

const encodeJpeg = (
  image: HTMLImageElement,
  maxEdge: number,
  quality: number,
): Promise<Blob | null> => {
  const { width, height } = fitWithin(
    image.naturalWidth,
    image.naturalHeight,
    maxEdge,
  );
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    return Promise.resolve(null);
  }
  // JPEG has no alpha: a transparent PNG would otherwise come out black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  return new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
};

/**
 * Turns a picked or pasted file into what the ticket endpoint accepts. A
 * small PNG/JPEG/WebP is sent untouched; anything else is re-encoded as JPEG,
 * stepping quality and then size down until it fits the per-file limit.
 */
export const prepareScreenshot = async (
  file: File,
): Promise<PreparedScreenshot> => {
  if (!file.type.startsWith("image/")) {
    throw new Error(`${file.name || "That file"} is not an image.`);
  }

  if (isAcceptedType(file.type) && file.size <= TICKET_SCREENSHOT_MAX_BYTES) {
    const previewUrl = await readAsDataUrl(file);
    return {
      filename: file.name || "screenshot",
      content_type: file.type,
      data: stripDataPrefix(previewUrl),
      previewUrl,
    };
  }

  const image = await loadImage(file);
  for (const maxEdge of EDGE_STEPS) {
    for (const quality of QUALITY_STEPS) {
      const blob = await encodeJpeg(image, maxEdge, quality);
      if (!blob) {
        throw new Error("This browser could not compress the image.");
      }
      if (blob.size <= TICKET_SCREENSHOT_MAX_BYTES) {
        const previewUrl = await readAsDataUrl(blob);
        return {
          filename: withJpegExtension(file.name),
          content_type: "image/jpeg",
          data: stripDataPrefix(previewUrl),
          previewUrl,
        };
      }
    }
  }
  throw new Error(`${file.name} could not be compressed under 1 MB.`);
};
